import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { Preferences } from "@capacitor/preferences";

export const REMINDER_STORAGE_KEY = "chevalet_reminder_choice_v1";
const REMINDER_ID = 9001;

export function isNative() {
  return Capacitor.isNativePlatform();
}

export async function ensureNotifPermission() {
  if (!isNative()) return { granted: false, reason: "web" };

  // Sur Capacitor, "requestPermissions" renvoie { display: "granted" | "denied" }
  const perm = await LocalNotifications.requestPermissions();
  return { granted: perm.display === "granted" };
}

export async function cancelDailyReminder() {
  if (!isNative()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: REMINDER_ID }] });
  } catch (_) {
    // si rien à annuler, on ignore
  }
}

/**
 * Programme un rappel quotidien.
 * @param {{hour:number, minute:number, language:"fr"|"en"}} param0
 */
export async function scheduleDailyReminder({ hour, minute = 0, language = "fr" }) {
  if (!isNative()) return;

  const { granted } = await ensureNotifPermission();
  if (!granted) throw new Error("permission_denied");

  // Annule l'ancien rappel (si existant)
  await cancelDailyReminder();

  // Programme le nouveau
  await LocalNotifications.schedule({
    notifications: [
      {
        id: REMINDER_ID,
        title: language === "fr" ? "Chevalet quotidien" : "Daily Easel",
        body:
          language === "fr"
            ? "Parce qu’Allah aime la constance, même si elle est peu, prends simplement 2 minutes pour recentrer ton âme sur l’essentiel, bi idhniLlâh."
            : "Because Allah loves consistency, even if it is small, take a moment to refocus your soul on what truly matters, bi idhniLlâh.",
        schedule: {
          on: { hour, minute },
          repeats: true,
          allowWhileIdle: true,
        },
      },
    ],
  });
}

/**
 * Sauvegarde le choix de rappel (enabled + heure) dans Preferences.
 * @param {{enabled:boolean, hour?:number, minute?:number}} param0
 */
export async function saveReminderChoice({ enabled, hour = 21, minute = 0 }) {
  const payload = { enabled: !!enabled, hour, minute };
  await Preferences.set({
    key: REMINDER_STORAGE_KEY,
    value: JSON.stringify(payload),
  });
  return payload;
}

/**
 * Lit le choix de rappel sauvegardé.
 * @returns {Promise<{enabled:boolean, hour:number, minute:number} | null>}
 */
export async function loadReminderChoice() {
  const { value } = await Preferences.get({ key: REMINDER_STORAGE_KEY });
  if (!value) return null;

  try {
    const obj = JSON.parse(value);

    // format attendu: { enabled, hour, minute }
    if (typeof obj === "object" && obj) {
      return {
        enabled: obj.enabled !== false,
        hour: Number(obj.hour ?? 21),
        minute: Number(obj.minute ?? 0),
      };
    }
    return null;
  } catch (_) {
    return null;
  }
}

/**
 * À appeler au démarrage de l'app (dans App.js) pour réappliquer
 * automatiquement le rappel choisi.
 * @param {("fr"|"en")} language
 */
export async function applySavedReminder(language = "fr") {
  if (!isNative()) return { applied: false, reason: "web" };

  const choice = await loadReminderChoice();
  if (!choice) return { applied: false, reason: "no_saved_choice" };

  if (!choice.enabled) {
    await cancelDailyReminder();
    return { applied: true, reason: "disabled" };
  }

  // Si permission refusée => on ne force pas, on laisse l'app continuer
  const perm = await ensureNotifPermission();
  if (!perm.granted) return { applied: false, reason: "permission_denied" };

  await scheduleDailyReminder({
    hour: choice.hour,
    minute: choice.minute,
    language,
  });

  return { applied: true, reason: "scheduled" };
}

/**
 * Helper optionnel : définit + sauvegarde + applique (ou annule) en une fois.
 * @param {{enabled:boolean, hour?:number, minute?:number, language?:"fr"|"en"}} param0
 */
export async function setReminder({ enabled, hour = 21, minute = 0, language = "fr" }) {
  const saved = await saveReminderChoice({ enabled, hour, minute });

  if (!isNative()) return { ok: true, saved, applied: false, reason: "web" };

  if (!saved.enabled) {
    await cancelDailyReminder();
    return { ok: true, saved, applied: true, reason: "disabled" };
  }

  await scheduleDailyReminder({ hour: saved.hour, minute: saved.minute, language });
  return { ok: true, saved, applied: true, reason: "scheduled" };
}
